import type { WorkShiftClockAction } from './enums/work-shift-clock-action.js'

const MINUTES_PER_DAY = 24 * 60
const HALF_DAY_MINUTES = MINUTES_PER_DAY / 2
const TIME_OF_DAY_REGEX = /^(\d{2}):(\d{2})$/

const parseTimeOfDayMinutes = (time: string | null | undefined): number | null => {
  if (!time) return null
  const match = TIME_OF_DAY_REGEX.exec(time)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  // '24:00' est la représentation canonique de fin de journée (END_OF_DAY_TIME côté backend).
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes > 0)) return null
  return hours * 60 + minutes
}

/**
 * Écart signé `actual − reference` en minutes entre deux heures 'HH:mm', en choisissant le
 * représentant le plus proche de zéro (±12 h) : un pointage à 00:10 pour une fin prévue à 23:30
 * vaut +40 min, un clock-out à 23:59 pour une fin prévue à 01:00 vaut −61 min — jamais ±1400.
 * Indispensable dès qu'un créneau chevauche minuit, où les minutes-du-jour brutes divergent.
 */
export const computeTimeOfDayDeltaMinutes = (
  reference: string | null | undefined,
  actual: string | null | undefined,
): number | null => {
  const referenceMinutes = parseTimeOfDayMinutes(reference)
  const actualMinutes = parseTimeOfDayMinutes(actual)
  if (referenceMinutes === null || actualMinutes === null) return null
  const rawDelta = actualMinutes - referenceMinutes
  return ((rawDelta + HALF_DAY_MINUTES + 2 * MINUTES_PER_DAY) % MINUTES_PER_DAY) - HALF_DAY_MINUTES
}

interface LogicalClockLike {
  action: WorkShiftClockAction
}

interface LogicalClockShift<Clock extends LogicalClockLike> {
  clockValues?: Clock[] | null
  childShift?: { clockValues?: Clock[] | null } | null
}

const findClockByAction = <Clock extends LogicalClockLike>(
  clocks: Clock[] | null | undefined,
  action: WorkShiftClockAction,
): Clock | null => clocks?.find((clock) => clock.action === action) ?? null

/**
 * Clocks logiques d'un créneau, paire de nuit comprise. Un créneau à cheval sur minuit porte des
 * clocks techniques (clock-out synthétique à 23:59 sur le parent, clock-in synthétique à 00:00 sur
 * l'enfant) en plus des pointages réels : l'arrivée logique vit sur le parent et le départ logique
 * sur l'enfant (repli sur le parent si le départ a été pointé avant minuit). Même sémantique que
 * `getActualTimesFromWorkShift` côté backend (validateWorkShift).
 */
export const resolveLogicalShiftClocks = <Clock extends LogicalClockLike>(
  shift: LogicalClockShift<Clock>,
): { clockIn: Clock | null; clockOut: Clock | null } => {
  const childClocks = shift.childShift?.clockValues
  return {
    clockIn:
      findClockByAction(shift.clockValues, 'clock-in') ??
      findClockByAction(childClocks, 'clock-in'),
    clockOut:
      findClockByAction(childClocks, 'clock-out') ??
      findClockByAction(shift.clockValues, 'clock-out'),
  }
}
